import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, getSettings, fmtDate, fmtDayNum, fmtMonth } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function Clubs() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [handle, setHandle] = useState("KNDLA101");
  const { t, locale } = useLang();

  useEffect(() => {
    api
      .get("/events?type=club")
      .then((r) => setClubs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    getSettings().then((s) => setHandle(s.telegram_handle));
  }, []);

  return (
    <section id="clubs" data-testid="clubs-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 md:px-8 md:py-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-flame">{t("clubs.eyebrow")}</p>
          <h2 className="mt-3 font-syne text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
            {t("clubs.title")}
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-white/55">{t("clubs.sub")}</p>
      </motion.div>

      {loading ? (
        <div className="mt-14 space-y-4">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-white/5 bg-ash" />
          ))}
        </div>
      ) : clubs.length === 0 ? (
        <p data-testid="clubs-empty" className="mt-14 rounded-2xl border border-white/10 bg-ash p-10 text-center text-white/50">
          {t("clubs.empty")}
        </p>
      ) : (
        <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
          {clubs.map((c, i) => {
            const msg = encodeURIComponent(`Hi KNDLA! Save me a spot at "${c.title}" on ${fmtDate(c.date, "en-GB")}.`);
            return (
              <motion.a
                key={c.id}
                data-testid={`club-row-${c.id}`}
                href={`https://t.me/${handle}?text=${msg}`}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 px-2 py-7 transition-colors duration-300 hover:bg-white/[0.03] md:gap-8 md:px-4"
              >
                <div className="w-16 text-center">
                  <div className="text-outline font-syne text-4xl font-extrabold leading-none transition-all duration-300 group-hover:text-flame group-hover:[-webkit-text-stroke-width:0px] md:text-5xl">
                    {fmtDayNum(c.date)}
                  </div>
                  <div className="mt-1 text-[10px] font-bold tracking-[0.25em] text-white/50">{fmtMonth(c.date, locale)}</div>
                </div>
                <div className="min-w-0">
                  <h3 className="font-syne text-xl font-bold tracking-tight md:text-2xl">{c.title}</h3>
                  <p className="mt-1 text-sm text-white/50">
                    {c.time} · {c.location}
                  </p>
                  {c.description && (
                    <p className="mt-2 hidden max-w-xl text-sm leading-relaxed text-white/40 md:block">{c.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 md:gap-5">
                  <span className="hidden rounded-full border border-flame/40 px-3.5 py-1.5 text-xs font-bold text-flame sm:block">
                    {c.price > 0 ? `${c.currency}${c.price}` : t("clubs.freeEntry")}
                  </span>
                  <ArrowUpRight className="h-6 w-6 shrink-0 text-white/35 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-flame" />
                </div>
              </motion.a>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-xs uppercase tracking-[0.25em] text-white/30">{t("clubs.note")}</p>
    </section>
  );
}
